import { useEffect, useRef } from "react";
import Head from "next/head";
import { Provider, useDispatch, useSelector } from "react-redux";
import storeWrapper, { persistor, store } from "../utils/store/storeWrapper";
import preventer from "../utils/common/preventer";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "codemirror/lib/codemirror.css";
import "@toast-ui/editor/dist/toastui-editor.css";
import "../styles/global.scss";
import "../styles/editor.scss";
import "../styles/messenger.scss";
import "../styles/uprism.scss";
import { useRoomConnection } from "../utils/customHook/useRoomConnection";
import { SOCKET_EVENT } from "../utils/data/socket.enum";
import { RTCConfig } from "../utils/data/rtcConfig";
import { useCreateMediaStream } from "../utils/customHook/useCreateMediaStream";
import { socketId } from "../utils/data/socketID";
import {
  setCamStreamState,
  setScreenStreamState,
} from "../utils/store/actions/mediaStreamAction";
import { VideoStreamMerger } from "video-stream-merger";
import { useRouter } from "next/router";
import { setSocketViewerInfo } from "../utils/store/actions/socketAction";
import { PersistGate } from "redux-persist/integration/react";
import { receiveStatus } from "../utils/common/receiveStatus";
import { setCheck } from "../utils/store/actions/userStateAction";
import {
  addSentryBreadcrumb,
  setSentryExamContext,
} from "../utils/common/sentryContext";

let my_video_stream = null;
let resize_video_stream = null;
const NETWORK_PENDING_DELAY = 90 * 1000;

// 합성 된 stream을 담을 변수
let my_mix_stream;

// main_display 초기설정
let main_display = "CAMERA";

// 현재 비디오 화질(해상도 계산)
let my_video_quality = 2;

// 비디오 크기를 담을 변수
let video_width;
let video_height;

// 스크린 크기를 담을 변수
let screen_width;
let screen_height;

let peerConnections = [];
let peerSocketId = [];

let isShareViewFlag = false;

const VALID_REMOTE_DESCRIPTION_TYPES = ["offer", "answer"];

const isValidRemoteDescription = (description, expectedType) => {
  return (
    description &&
    VALID_REMOTE_DESCRIPTION_TYPES.includes(description.type) &&
    description.type === expectedType &&
    typeof description.sdp === "string" &&
    description.sdp.length > 0
  );
};

const warnInvalidRemoteDescription = (eventName, peerId, description) => {
  const remoteDescription = {
    peerId,
    type: description?.type,
    hasSdp: typeof description?.sdp === "string" && description.sdp.length > 0,
  };

  console.warn(`${eventName}: invalid remote description`, remoteDescription);
  addSentryBreadcrumb({
    category: "webrtc",
    message: `${eventName}: invalid remote description`,
    level: "warning",
    data: remoteDescription,
  });
};

const App = ({ Component, pageProps }) => {
  const dispatch = useDispatch();
  const router = useRouter();

  const timeOutDisconnected = useRef();
  const networkPendingTimer = useRef();
  const sentDeviceNoticeSocketId = useRef(null);
  const viewerRef = useRef(null);
  const videoRef = useRef(null);
  const screenRef = useRef(null);
  const userInfo = useSelector((state) => state.userInfo);
  const time = useSelector((state) => state.time);
  const { joinRoom, socket, sendToExamTopic } = useRoomConnection();
  const { video, audio, videoStream, screenStream } = useSelector(
    (state) => state.device,
  );
  const { myRoomId, myPeerId, con_my_peer_type, peerViewMod } = socketId(
    userInfo?.ROOM_IDX,
    userInfo?.Tester_IDX,
  );
  const socketData = useSelector((state) => state.socket);
  const { getWebcam, getScreen, getAudio } = useCreateMediaStream();
  const userState = useSelector((state) => state.userState);

  receiveStatus();

  const clearNetworkPendingTimer = () => {
    clearTimeout(networkPendingTimer.current);
    networkPendingTimer.current = null;
  };

  useEffect(() => {
    preventer();
  }, []);

  useEffect(() => {
    const isMainTestPage = () =>
      router.pathname === "/main" && !userInfo.isPreCheck;

    const handleOffline = () => {
      if (!isMainTestPage()) {
        return;
      }

      if (networkPendingTimer.current) {
        return;
      }

      networkPendingTimer.current = setTimeout(() => {
        dispatch(setCheck(false, "NETWORK"));
        sendToExamTopic(socketData?.viewerSocket?.viewerSocketId, "PAUSE");
        networkPendingTimer.current = null;
      }, NETWORK_PENDING_DELAY);
    };

    const handleOnline = () => {
      if (!isMainTestPage()) {
        return;
      }

      clearNetworkPendingTimer();
      if (!socket?.connected) {
        socket?.connect();
      }
      joinRoom();
      sendMyPeerId(socketData?.viewerSocket?.viewerSocketId);
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      clearNetworkPendingTimer();
    };
  }, [
    router.pathname,
    userInfo.isPreCheck,
    socketData?.viewerSocket?.viewerSocketId,
  ]);

  useEffect(() => {
    setSentryExamContext({ userInfo, router, time });
  }, [
    userInfo?.Tester_IDX,
    userInfo?.Tester_Name,
    userInfo?.ROOM_IDX,
    userInfo?.isPreCheck,
    router.pathname,
    router.query.mode,
    router.query.type,
    Math.floor(time.afterTime / 30),
    time.timeLimit,
  ]);

  const mergeVideoStream = async (videoStream) => {
    if (videoRef.current) {
      videoRef.current.srcObject = videoStream;

      my_video_stream = videoStream;
      await new Promise(
        (resolve) => (videoRef.current.onloadedmetadata = resolve),
      );
      video_width = videoRef.current.videoWidth;
      video_height = videoRef.current.videoHeight;

      joinRoom();
      //Create a new video merger.
      if (main_display) {
        //CAMERA
        resize_video_stream = new VideoStreamMerger({
          width: 160,
          height: 120,
          fps: 4,
        });
        // Start the merging.
        resize_video_stream.start();
        my_mix_stream = resize_video_stream.result;

        resizeVideoStream(main_display, my_video_quality);
      }
    }
  };

  useEffect(() => {
    if (
      !userInfo.isPreCheck &&
      userState.isCheckDevice &&
      screenStream?.id &&
      videoStream?.id
    ) {
      isShareViewFlag = true;
      screenRef.current.srcObject = screenStream;
      new Promise((resolve) => (screenRef.current.onloadedmetadata = resolve));
      screen_width = screenRef.current.clientWidth;
      screen_height = screenRef.current.clientHeight;

      videoRef.current.srcObject = videoStream;

      if (!userInfo.isPreCheck) {
        mergeVideoStream(videoStream);
      }
    }
  }, [
    screenStream,
    videoStream,
    socketData?.viewerSocket?.viewerSocketId,
    userState.isCheckDevice,
  ]);

  useEffect(() => {
    if (
      !userInfo.isPreCheck &&
      !isShareViewFlag &&
      router.pathname !== "/" &&
      router.pathname !== "/prevInfo" &&
      router.pathname !== "/device" &&
      router.pathname !== "/register" &&
      router.pathname !== "/report/doc" &&
      router.pathname !== "/report/chart" &&
      !router.pathname.includes("admin") &&
      !screenStream?.id &&
      !videoStream?.id
    ) {
      isShareViewFlag = true;
      getScreen((stream) => {
        screenRef.current.srcObject = stream;
        new Promise(
          (resolve) => (screenRef.current.onloadedmetadata = resolve),
        );
        screen_width = screenRef.current.clientWidth;
        screen_height = screenRef.current.clientHeight;
        dispatch(setScreenStreamState(stream));
        getWebcam((stream) => {
          videoRef.current.srcObject = stream;
          dispatch(setCamStreamState(stream));
          if (!userInfo.isPreCheck) {
            mergeVideoStream(stream);
          }
        });
      });
    }
    return () => {
      socket?.off(SOCKET_EVENT.EXAM);
    };
  }, [router.pathname, socketData?.viewerSocket?.viewerSocketId]);

  const deletePeerConnection = () => {
    if (Object.keys(peerConnections).length > 0) {
      Object.keys(peerConnections).forEach((peerId) => {
        peerConnections[peerId].close();
        delete peerConnections[peerId];
      });
    }
  };

  const closePeerConnection = (peerId) => {
    if (peerConnections[peerId]) {
      peerConnections[peerId].close();
      delete peerConnections[peerId];
    }
    delete peerSocketId[peerId];
  };

  const isStreamLive = (stream) => {
    const tracks = stream?.getTracks?.() || [];
    return tracks.length > 0 && tracks.every((track) => track.readyState === "live");
  };

  const requestScreenStream = () =>
    new Promise((resolve) => {
      getScreen((stream) => {
        if (screenRef.current) {
          screenRef.current.srcObject = stream;
          new Promise(
            (loadedResolve) =>
              (screenRef.current.onloadedmetadata = loadedResolve),
          );
          screen_width = screenRef.current.clientWidth;
          screen_height = screenRef.current.clientHeight;
        }
        dispatch(setScreenStreamState(stream));
        resolve(stream);
      });
    });

  const requestWebcamStream = () =>
    new Promise((resolve) => {
      getWebcam((stream) => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        dispatch(setCamStreamState(stream));
        resolve(stream);
      });
    });

  const ensureMediaStreamsReady = async () => {
    const currentScreenStream = screenRef.current?.srcObject || screenStream;
    const currentVideoStream = videoRef.current?.srcObject || videoStream;
    const shouldRefreshScreen = !isStreamLive(currentScreenStream);
    const shouldRefreshVideo = !isStreamLive(currentVideoStream);

    if (shouldRefreshScreen) {
      await requestScreenStream();
    }

    if (shouldRefreshVideo) {
      await requestWebcamStream();
    }

    return (
      isStreamLive(screenRef.current?.srcObject) &&
      isStreamLive(videoRef.current?.srcObject)
    );
  };

  const refreshMergedVideoStream = () => {
    if (!videoRef.current?.srcObject) {
      return;
    }

    if (resize_video_stream?.destroy) {
      resize_video_stream.destroy();
    }

    resize_video_stream = new VideoStreamMerger({
      width: 160,
      height: 120,
      fps: 4,
    });
    resize_video_stream.start();
    my_mix_stream = resize_video_stream.result;
    resizeVideoStream(main_display, my_video_quality);
  };

  const sendMyPeerId = (targetSocketId) => {
    if (!targetSocketId) {
      return;
    }

    socket?.emit(SOCKET_EVENT.SEND_ID, {
      peerId: myPeerId,
      peerType: con_my_peer_type,
      targetSocketId,
    });
  };

  const isMainExam = () => router.pathname === "/main" && !userInfo.isPreCheck;

  const getManagerPeerId = () =>
    `${process.env.NEXT_PUBLIC_SITE_ID}-${userInfo.ROOM_IDX}-MANAGER`;

  const isManagerPeer = (peerId, peerType) => {
    return peerType === "MANAGER" || peerId === getManagerPeerId();
  };

  const updateViewerConnection = (peerId, socketId, peerType = "MANAGER") => {
    if (!socketId || !isManagerPeer(peerId, peerType)) {
      return;
    }

    peerSocketId[peerId] = socketId;
    dispatch(
      setSocketViewerInfo({
        viewerPeerId: peerId,
        viewerSocketId: socketId,
        viewerPeerType: peerType,
      }),
    );
  };

  const sendDeviceNoticeToViewer = (targetSocketId) => {
    if (
      !targetSocketId ||
      sentDeviceNoticeSocketId.current === targetSocketId ||
      router.pathname !== "/main" ||
      userInfo.isPreCheck ||
      !window.navigator.userAgent.includes("Mac")
    ) {
      return;
    }

    socket?.emit(SOCKET_EVENT.CHAT, "to", {
      targetSocketId,
      peerId: myPeerId,
      userName: userInfo.Tester_Name,
      message: {
        text: `<strong>[알림]\n Mac OS 사용 응시자 </strong>`,
        type: "chat",
      },
    });
    sentDeviceNoticeSocketId.current = targetSocketId;
  };

  const handleVideoReconnect = async (targetSocketId) => {
    deletePeerConnection();
    await ensureMediaStreamsReady();
    refreshMergedVideoStream();
    joinRoom();
    const viewerSocketId =
      targetSocketId || socketData?.viewerSocket?.viewerSocketId;
    sendMyPeerId(viewerSocketId);
  };

  // 비디오 사이즈 변경 함수
  const resizeVideoStream = async (mainStream, quality) => {
    let resize_width;
    let resize_height;

    let resize_video_width;
    let resize_video_height;
    let resize_screen_width;
    let resize_screen_height;

    if (mainStream === "SCREEN") {
      quality = quality + 2;
    }

    resize_width = 80 * quality;
    resize_height = 60 * quality;

    resize_video_width = 80 * quality;
    resize_video_height = 45 * quality;

    resize_screen_width = 80 * quality;
    resize_screen_height = 45 * quality;

    resize_video_stream.setOutputSize(resize_width, resize_height);
    resize_video_stream._frameCount = 0;

    if (mainStream === "CAMERA" || mainStream === "CAMERA-ONLY") {
      resize_video_stream.addStream(videoRef.current.srcObject, {
        x: 0,
        y: resize_height - resize_video_height,
        width: resize_video_width,
        height: resize_video_height,
        mute: false,
      });

      if (mainStream === "CAMERA" && screenRef.current.srcObject) {
        resize_video_stream.addStream(screenRef.current.srcObject, {
          x: 0,
          y: 0,
          width: resize_screen_width * 0.45,
          height: resize_screen_height * 0.45,
          mute: true,
        });
      }
    } else if (mainStream === "SCREEN" || mainStream === "SCREEN-ONLY") {
      resize_video_stream.addStream(screenRef.current.srcObject, {
        x: 0,
        y: resize_height - resize_screen_height + resize_screen_height * 0.1,
        width: resize_screen_width,
        height: resize_screen_height - resize_screen_height * 0.1,
        mute: true,
      });

      if (mainStream === "SCREEN-ONLY") {
        resize_video_width = 1;
        resize_video_height = 1;
      }
      resize_video_stream.addStream(videoRef.current.srcObject, {
        x: 0,
        y: 0,
        width: resize_video_width * 0.45,
        height: resize_video_height * 0.45,
        mute: false,
      });
    }
    if (resize_video_stream._streams.length > 2) {
      resize_video_stream._streams.splice(0, 2);
    }
  };

  useEffect(() => {
    if (!socket) return;
    //영상 위치/화질 변경
    socket?.on(SOCKET_EVENT.USER_VIDEO, async (message) => {
      let videoView = message.videoView;
      let videoQuality = message.videoQuality;

      if (my_mix_stream?.id) {
        if (videoQuality > 0) {
          if (my_mix_stream.getVideoTracks()[0].enabled === false) {
            my_mix_stream.getVideoTracks()[0].enabled = true;
          }
          await resizeVideoStream(videoView, videoQuality);
        } else {
          my_mix_stream.getVideoTracks()[0].enabled = false;
        }
      }
    });

    //사용자 ID 수신
    socket?.on(SOCKET_EVENT.USER_ID, (message) => {
      const conPeerId = message.peerId;
      const conPeerSocketId = message.socketId;
      const conPeerType = message.peerType;

      if (isManagerPeer(conPeerId, conPeerType)) {
        //감독관의 peerId, socketId 저장
        updateViewerConnection(conPeerId, conPeerSocketId, conPeerType);
        sendDeviceNoticeToViewer(conPeerSocketId);
      }
    });

    socket?.on(
      SOCKET_EVENT.USER_OFFER,
      async (peerId, socketId, videoAnswer, description) => {
        if (isManagerPeer(peerId)) {
          updateViewerConnection(peerId, socketId);
        }

        if (!isValidRemoteDescription(description, "offer")) {
          warnInvalidRemoteDescription(
            SOCKET_EVENT.USER_OFFER,
            peerId,
            description,
          );
          return;
        }

        const viewMode = "CAMERA";
        const videoQuality = 2;
        const isMediaReady = await ensureMediaStreamsReady();

        if (!isMediaReady) {
          console.warn("USER_OFFER: media stream is not ready");
          return;
        }

        if (main_display !== viewMode || my_video_quality !== videoQuality) {
          //메인 디스플레이 저장(CAMERA / SCREEN / CAMERA-ONLY / SCREEN-ONLY)
          main_display = viewMode;
          my_video_quality = videoQuality; //현재 화질을 저장
          //전송될 영상 사이즈 변경
          resizeVideoStream(viewMode, videoQuality);
        }
        if (!my_mix_stream?.id || !isStreamLive(my_mix_stream)) {
          refreshMergedVideoStream();
        }

        //연결점을 만든다.
        peerConnections[peerId] = new RTCPeerConnection(RTCConfig);

        //영상 교환일 경우에만
        if (videoAnswer === "Y" && my_mix_stream?.id) {
          //내 영상과 오디오에서 오는 stream을 가져다가 연결을 만든다.
          //peer to peer 연결 안에다가 영상과 오디오를 넣는다.
          my_mix_stream
            .getTracks()
            .forEach((track) =>
              peerConnections[peerId].addTrack(track, my_mix_stream),
            );
        }
        peerConnections[peerId].oniceconnectionstatechange = function () {
          const iceState = peerConnections[peerId]?.iceConnectionState;
          console.log(
            "ICE state...: ",
            iceState,
          );
          if (iceState === "failed" || iceState === "closed") {
            closePeerConnection(peerId);
          }
        };

        peerConnections[peerId]
          .setRemoteDescription(description)
          .then(() => peerConnections[peerId].createAnswer())
          .then((sdp) => peerConnections[peerId].setLocalDescription(sdp))
          .then(() => {
            socket.emit(
              SOCKET_EVENT.ANSWER,
              myPeerId,
              socketId,
              peerConnections[peerId].localDescription,
            );
          })
          .catch((err) => {
            console.error("USER_OFFER: setRemoteDescription failed", err);
            closePeerConnection(peerId);
          });

        peerConnections[peerId].ontrack = (event) => {
          let stream = event.streams[0];
          if (stream) {
            if (event.track.kind === "video") {
              //감독관 영상 출력
              viewerRef.current.srcObject = stream;
            }
          }
        };

        peerConnections[peerId].onicecandidate = (event) => {
          if (event.candidate && userInfo?.Tester_IDX && userInfo?.ROOM_IDX) {
            socket.emit(
              SOCKET_EVENT.CANDIDATE,
              myPeerId,
              socketId,
              event.candidate,
            );
          }
        };
      },
    );

    socket?.on(SOCKET_EVENT.USER_CANDIDATE, (peerId, candidate) => {
      if (!peerConnections[peerId]) {
        console.warn("USER_CANDIDATE: peerConnection not found for", peerId);
        return;
      }

      peerConnections[peerId]
        .addIceCandidate(new RTCIceCandidate(candidate))
        .catch((err) => {
          console.error("USER_CANDIDATE: addIceCandidate failed", err);
        });
    });

    socket?.on(SOCKET_EVENT.VIDEO_CALLED, async (peerId, socketId, message) => {
      peerSocketId[peerId] = socketId;
      if (isManagerPeer(peerId)) {
        updateViewerConnection(peerId, socketId);
      }

      const sendVideo = message.sendVideo;
      const getVideo = message.getVideo;

      if (peerConnections[peerId]) {
        closePeerConnection(peerId);
      }

      const isMediaReady = await ensureMediaStreamsReady();

      if (!isMediaReady) {
        console.warn("VIDEO_CALLED: media stream is not ready");
        return;
      }

      if (!my_mix_stream?.id || !isStreamLive(my_mix_stream)) {
        refreshMergedVideoStream();
      }

      if (!my_mix_stream?.id || !isStreamLive(my_mix_stream)) {
        console.warn("VIDEO_CALLED: media stream is not ready");
        return;
      }

      peerConnections[peerId] = new RTCPeerConnection(RTCConfig);

      if (getVideo === "Y") {
        my_mix_stream
          ?.getTracks()
          .forEach((track) =>
            peerConnections[peerId].addTrack(track, my_mix_stream),
          );
      } else {
        my_mix_stream
          .getTracks()
          .forEach((track) => peerConnections[peerId].addTrack(track));
      }
      peerConnections[peerId].onicecandidate = (event) => {
        if (event.candidate && userInfo?.Tester_IDX && userInfo?.ROOM_IDX) {
          socket.emit(
            SOCKET_EVENT.CANDIDATE,
            myPeerId,
            socketId,
            event.candidate,
          );
        }
      };

      peerConnections[peerId]
        .createOffer()
        .then((sdp) => peerConnections[peerId].setLocalDescription(sdp))
        .then(() => {
          socket.emit(
            SOCKET_EVENT.OFFER,
            myPeerId,
            socketId,
            sendVideo,
            peerConnections[peerId].localDescription,
          );
        });
      peerConnections[peerId].oniceconnectionstatechange = function () {
        const iceState = peerConnections[peerId]?.iceConnectionState;
        console.log("ICE state: ", iceState);
        if (iceState === "failed" || iceState === "closed") {
          closePeerConnection(peerId);
        }
      };
    });

    socket?.on(SOCKET_EVENT.USER_ANSWER, (peerId, description) => {
      if (!peerConnections[peerId]) {
        console.warn("USER_ANSWER: peerConnection not found for", peerId);
        return;
      }
      if (peerConnections[peerId].signalingState !== "have-local-offer") {
        console.warn(
          "USER_ANSWER: unexpected signalingState",
          peerConnections[peerId].signalingState,
          "for peer",
          peerId
        );
        return;
      }
      if (!isValidRemoteDescription(description, "answer")) {
        warnInvalidRemoteDescription(
          SOCKET_EVENT.USER_ANSWER,
          peerId,
          description,
        );
        return;
      }
      peerConnections[peerId]
        .setRemoteDescription(description)
        .catch((err) => {
          console.error("USER_ANSWER: setRemoteDescription failed", err);
        });
      peerConnections[peerId].ontrack = (event) => {
        let stream = event.streams[0];

        if (stream) {
          if (event.track.kind === "video") {
            viewerRef.current.srcObject = stream;
          }
        }
      };
    });

    socket?.on(SOCKET_EVENT.USER_REFRESH, () => {
      socket.close();
      //소켓 접속
      socket.connect();
      //룸 접속
      handleVideoReconnect();
    });
    const handleSocketReconnect = () => {
      socket.close();
      socket.connect(); //소켓 접속
      handleVideoReconnect(); // 룸 접속 및 화상 재연결 정보 재전송
    };

    //소켓 재연결 이벤트시 (끊겼다가 다시 접속)
    socket.io.on("reconnect", handleSocketReconnect);

    //사용자 룸 입장
    socket?.on(SOCKET_EVENT.USER_CONNECTED, (message) => {
      const conPeerId = message.peerId;
      const conPeerType = message.peerType;
      const conPeerSocketId = message.socketId;
      if (isManagerPeer(conPeerId, conPeerType)) {
        clearTimeout(timeOutDisconnected.current);
        //응시자 접속이 아니라면(감독관 또는 운영자)
        updateViewerConnection(conPeerId, conPeerSocketId, conPeerType);
        sendMyPeerId(conPeerSocketId);
        sendDeviceNoticeToViewer(conPeerSocketId);
      }
    });

    //소켓 재연결 이벤트시
    socket?.on(SOCKET_EVENT.VIDEO_REFRESH, (message) => {
      handleVideoReconnect(message?.socketId || message?.targetSocketId);
    });

    return () => {
      socket?.off(SOCKET_EVENT.USER_CONNECTED);
      socket?.off(SOCKET_EVENT.USER_REFRESH);
      socket?.off(SOCKET_EVENT.USER_VIDEO);
      socket?.off(SOCKET_EVENT.USER_ID);
      socket?.off(SOCKET_EVENT.USER_OFFER);
      socket?.off(SOCKET_EVENT.VIDEO_CALLED);
      socket?.off(SOCKET_EVENT.USER_CANDIDATE);
      socket?.off(SOCKET_EVENT.USER_ANSWER);
      socket?.off(SOCKET_EVENT.USER_SOUND);
      socket?.off(SOCKET_EVENT.VIDEO_REFRESH);
      socket.io.off("reconnect", handleSocketReconnect);
    };
  }, [
    userInfo?.Tester_IDX,
    userInfo?.ROOM_IDX,
    userInfo?.isPreCheck,
    socketData?.viewerSocket?.viewerSocketId,
    router.pathname,
  ]);

  useEffect(() => {
    //탭 이동 방지
    if (router.pathname === "/main" && !sessionStorage.getItem("token")) {
      setCheck(false);
      router.push("/");
    }
  }, [router.pathname]);

  useEffect(() => {
    if (!socket) return;
    socket?.on(SOCKET_EVENT.USER_DISCONNECTED, (peerId, peerType, socketId) => {
      //응시자가 아니라면(감독관 또는 운영자)
      if (peerType !== "TESTER") {
        const currentViewerPeerId = socketData?.viewerSocket?.viewerPeerId;
        const currentViewerSocketId = socketData?.viewerSocket?.viewerSocketId;
        const isCurrentViewer =
          peerSocketId[peerId] === socketId ||
          currentViewerSocketId === socketId ||
          (currentViewerPeerId === peerId &&
            (!currentViewerSocketId || currentViewerSocketId === socketId)) ||
          (!currentViewerSocketId && isManagerPeer(peerId, peerType));

        if (isCurrentViewer) {
          //커넥션 ID 삭제
          closePeerConnection(peerId);
          //감독관이 끊겼을 경우
          if (router.pathname === "/main") {
            timeOutDisconnected.current = setTimeout(() => {
              dispatch(setCheck(false));
              sendToExamTopic(socketData?.viewerSocket?.viewerSocketId, "PAUSE");
              dispatch(
                setSocketViewerInfo({
                  viewerPeerId: null,
                  viewerSocketId: null,
                  viewerPeerType: null,
                }),
              );
            }, 180000);
          }
        }
      }
    });
    return () => {
      socket?.off(SOCKET_EVENT.EXAM);
      socket?.off(SOCKET_EVENT.USER_DISCONNECTED);
    };
  }, [socket, router, socketData?.viewerSocket?.viewerSocketId]);

  return (
    <Provider store={store}>
      <PersistGate persistor={persistor} loading={null}>
        <Head>
          <title>VAC</title>
        </Head>
        <Component {...pageProps} />
        <div
          style={{
            position: "fixed",
            zIndex: -1000000,
            top: 0,
            right: 0,
            height: "100px",
            opacity: 0,
            pointerEvents: "none",
          }}
        >
          <video ref={videoRef} muted autoPlay />
          <video ref={viewerRef} muted autoPlay />
          <video ref={screenRef} muted autoPlay />
        </div>
      </PersistGate>
    </Provider>
  );
};

export default storeWrapper.withRedux(App);
